/**
 * LLM Configurations Redux Slice
 */
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import llmConfigsApi from "../../api/llmConfigsApi";
import {
  LLMConfiguration,
  LLMConfigCreate,
  LLMConfigUpdate,
  TestConnectionRequest,
  TestConnectionResponse,
} from "../../types/llm.types";

interface LLMConfigsState {
  configs: LLMConfiguration[];
  loading: boolean;
  saving: boolean;
  testing: boolean;
  testResult: TestConnectionResponse | null;
  error: string | null;
}

const initialState: LLMConfigsState = {
  configs: [],
  loading: false,
  saving: false,
  testing: false,
  testResult: null,
  error: null,
};

// Async thunks
export const fetchAllConfigs = createAsyncThunk(
  "llmConfigs/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await llmConfigsApi.getAllConfigs();
      return response.configs;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to fetch configurations"
      );
    }
  }
);

export const createOrUpdateConfig = createAsyncThunk(
  "llmConfigs/createOrUpdate",
  async (config: LLMConfigCreate, { rejectWithValue }) => {
    try {
      const response = await llmConfigsApi.createOrUpdateConfig(config);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to save configuration"
      );
    }
  }
);

export const deleteConfig = createAsyncThunk(
  "llmConfigs/delete",
  async (config_id: string, { rejectWithValue }) => {
    try {
      await llmConfigsApi.deleteConfig(config_id);
      return config_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to delete configuration"
      );
    }
  }
);

export const setDefaultConfig = createAsyncThunk(
  "llmConfigs/setDefault",
  async (config_id: string, { rejectWithValue }) => {
    try {
      await llmConfigsApi.setDefaultConfig(config_id);
      return config_id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to set default"
      );
    }
  }
);

export const testConnection = createAsyncThunk(
  "llmConfigs/testConnection",
  async (testData: TestConnectionRequest, { rejectWithValue }) => {
    try {
      const response = await llmConfigsApi.testConnection(testData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail || "Failed to test connection"
      );
    }
  }
);

const llmConfigsSlice = createSlice({
  name: "llmConfigs",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTestResult: (state) => {
      state.testResult = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all configs
    builder.addCase(fetchAllConfigs.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAllConfigs.fulfilled, (state, action) => {
      state.loading = false;
      state.configs = action.payload;
    });
    builder.addCase(fetchAllConfigs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create or update config
    builder.addCase(createOrUpdateConfig.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(createOrUpdateConfig.fulfilled, (state, action) => {
      state.saving = false;
      // For Azure: match by provider + deployment_name, for others: match by provider + null deployment_name
      const index = state.configs.findIndex(
        (c) => c.id === action.payload.id
      );
      if (index >= 0) {
        state.configs[index] = action.payload;
      } else {
        state.configs.push(action.payload);
      }
    });
    builder.addCase(createOrUpdateConfig.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });

    // Delete config
    builder.addCase(deleteConfig.fulfilled, (state, action) => {
      state.configs = state.configs.filter((c) => c.id !== action.payload);
    });

    // Set default
    builder.addCase(setDefaultConfig.fulfilled, (state, action) => {
      state.configs = state.configs.map((c) => ({
        ...c,
        is_default: c.id === action.payload,
      }));
    });

    // Test connection
    builder.addCase(testConnection.pending, (state) => {
      state.testing = true;
      state.testResult = null;
    });
    builder.addCase(testConnection.fulfilled, (state, action) => {
      state.testing = false;
      state.testResult = action.payload;
    });
    builder.addCase(testConnection.rejected, (state, action) => {
      state.testing = false;
      state.testResult = {
        success: false,
        message: action.payload as string,
      };
    });
  },
});

export const { clearError, clearTestResult } = llmConfigsSlice.actions;
export default llmConfigsSlice.reducer;
